import React, { useState, useEffect } from "react";
import { Container, Row, Col, Modal, Button, Card, Table, Form, Badge, Alert, InputGroup } from "react-bootstrap";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import { 
  FaVideo, 
  FaFileAlt,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaEyeSlash,
  FaFilter,
  FaSearch,
  FaUpload,
  FaDownload
} from "react-icons/fa";

import {
  whoami,
  getCountries,
  getSandboxContent,
  createSandboxContent,
  updateSandboxContent,
  deleteSandboxContent,
  activateSandboxContent,
  deactivateSandboxContent,
  getPublicSandboxContent,
  getPublicModalContent
} from "../services/APIClient";

import "../components/SchoolAdmin.css";

function SandboxContentManagement() {
  const [teacherid, setTeacherid] = useState(101);
  const [name, setName] = useState("Admin User");
  
  // Data states
  const [content, setContent] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    countryid: "",
    context: "",
    foldername: "",
    search: ""
  });
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [modalMode, setModalMode] = useState("create");

  useEffect(() => {
    whoami().then((response) => {
      setTeacherid(response.data.stakeholder.id);
      setName(response.data.stakeholder.name);
    });
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [content, filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [contentData, countriesData] = await Promise.all([
        getSandboxContent(),
        getCountries()
      ]);
      setContent(contentData);
      setCountries(countriesData);
    } catch (error) {
      showAlert("Error loading data: " + error.message, "danger");
    }
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...content];

    if (filters.countryid) {
      filtered = filtered.filter(item => item.countryid === filters.countryid);
    }

    if (filters.context) {
      filtered = filtered.filter(item => item.context === filters.context);
    }

    if (filters.foldername) {
      filtered = filtered.filter(item => 
        item.foldername.toLowerCase().includes(filters.foldername.toLowerCase())
      );
    }

    if (filters.search) {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.foldername.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    setFilteredContent(filtered);
  };

  const showAlert = (message, variant = "success") => {
    setAlert({ message, variant });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleAdd = () => {
    setEditingItem(null);
    setModalMode("create");
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setModalMode("edit");
    setShowModal(true);
  };

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await deleteSandboxContent(id);
        showAlert("Content deleted successfully");
        loadData();
      } catch (error) {
        showAlert(`Error deleting: ${error.message}`, "danger");
      }
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      if (currentStatus) {
        await deactivateSandboxContent(id);
      } else {
        await activateSandboxContent(id);
      }
      showAlert(`Content ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      loadData();
    } catch (error) {
      showAlert(`Error toggling status: ${error.message}`, "danger");
    }
  };

  const getCountryName = (id) => {
    return countries.find(c => c.id === id)?.name || "Unknown";
  };

  const getIcon = (type) => {
    return type === "video" ? <FaVideo /> : <FaFileAlt />;
  };

  const stats = {
    total: content.length,
    active: content.filter(c => c.isactive).length,
    sandbox: content.filter(c => c.context === "sandbox").length,
    modal: content.filter(c => c.context === "modal").length,
    folders: [...new Set(content.map(c => c.foldername))].length
  };

  return (
    <div>
      <Container fluid className="class-admin-container">
        <HeadingBanner name={`Welcome ${name}!`} isLoggedIn={true} cloud={true} teacherid={teacherid} />

        <Row className="class-admin-row">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">

            <div className="class-admin-header">
              <h2 className="class-admin-page-title">Sandbox Content Management</h2>
              <p className="class-admin-page-subtitle">Manage videos, documents, and content for all countries</p>
            </div>

            {alert && (
              <Alert variant={alert.variant} onClose={() => setAlert(null)} dismissible className="mb-4">
                {alert.message}
              </Alert>
            )}

            {/* Stats Cards */}
            <Row className="mb-4">
              <Col md={2}>
                <Card className="text-center">
                  <Card.Body>
                    <h3>{stats.total}</h3>
                    <small className="text-muted">Total Items</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center">
                  <Card.Body>
                    <h3>{stats.active}</h3>
                    <small className="text-muted">Active</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center">
                  <Card.Body>
                    <h3>{stats.sandbox}</h3>
                    <small className="text-muted">Sandbox</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center">
                  <Card.Body>
                    <h3>{stats.modal}</h3>
                    <small className="text-muted">Modal</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center">
                  <Card.Body>
                    <h3>{stats.folders}</h3>
                    <small className="text-muted">Folders</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center">
                  <Card.Body>
                    <h3>{countries.length}</h3>
                    <small className="text-muted">Countries</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Action Buttons */}
            <div className="mb-3 d-flex gap-2">
              <Button variant="primary" onClick={handleAdd}>
                <FaPlus className="me-2" />
                Add Content
              </Button>
              <Button variant="secondary" onClick={() => setShowBulkImport(true)}>
                <FaUpload className="me-2" />
                Bulk Import
              </Button>
              <Button variant="info" onClick={() => setShowPreview(true)}>
                <FaEye className="me-2" />
                Preview Content
              </Button>
            </div>

            {/* Filters */}
            <Card className="mb-4">
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>
                        <FaFilter className="me-2" />
                        Country
                      </Form.Label>
                      <Form.Select
                        value={filters.countryid}
                        onChange={(e) => setFilters({...filters, countryid: e.target.value})}
                      >
                        <option value="">All Countries</option>
                        {countries.map(country => (
                          <option key={country.id} value={country.id}>{country.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Context</Form.Label>
                      <Form.Select
                        value={filters.context}
                        onChange={(e) => setFilters({...filters, context: e.target.value})}
                      >
                        <option value="">All</option>
                        <option value="sandbox">Sandbox</option>
                        <option value="modal">Modal</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Folder</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Filter by folder..."
                        value={filters.foldername}
                        onChange={(e) => setFilters({...filters, foldername: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>
                        <FaSearch className="me-2" />
                        Search
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search title or folder..."
                        value={filters.search}
                        onChange={(e) => setFilters({...filters, search: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={1} className="d-flex align-items-end">
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => setFilters({ countryid: "", context: "", foldername: "", search: "" })}
                    >
                      Clear
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Content Table */}
            <Card>
              <Card.Header>
                <h5 className="mb-0">Content Items ({filteredContent.length})</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Title</th>
                      <th>Folder</th>
                      <th>Country</th>
                      <th>Context</th>
                      <th>Order</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="text-center">Loading...</td>
                      </tr>
                    ) : filteredContent.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center text-muted">No content found</td>
                      </tr>
                    ) : (
                      filteredContent.map(item => (
                        <tr key={item.id}>
                          <td>{getIcon(item.contenttype)} {item.contenttype}</td>
                          <td>{item.title}</td>
                          <td><Badge bg="secondary">{item.foldername}</Badge></td>
                          <td>{getCountryName(item.countryid)}</td>
                          <td><Badge bg={item.context === "sandbox" ? "primary" : "info"}>{item.context}</Badge></td>
                          <td>{item.displayorder}</td>
                          <td>
                            <Badge bg={item.isactive ? "success" : "secondary"}>
                              {item.isactive ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td>
                            <Button variant="link" size="sm" onClick={() => handleEdit(item)} title="Edit">
                              <FaEdit />
                            </Button>
                            <Button 
                              variant="link" 
                              size="sm" 
                              onClick={() => handleToggleActive(item.id, item.isactive)}
                              title={item.isactive ? "Deactivate" : "Activate"}
                            >
                              {item.isactive ? <FaEyeSlash /> : <FaEye />}
                            </Button>
                            <Button 
                              variant="link" 
                              size="sm" 
                              className="text-danger"
                              onClick={() => handleDelete(item.id, item.title)}
                              title="Delete"
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

          </Col>
        </Row>
      </Container>

      {/* Create/Edit Modal */}
      <ContentModal
        show={showModal}
        onHide={() => setShowModal(false)}
        editingItem={editingItem}
        mode={modalMode}
        countries={countries}
        onSuccess={() => {
          setShowModal(false);
          loadData();
          showAlert(`Content ${modalMode === "create" ? "created" : "updated"} successfully`);
        }}
      />

      {/* Bulk Import Modal */}
      <BulkImportModal
        show={showBulkImport}
        onHide={() => setShowBulkImport(false)}
        countries={countries}
        onSuccess={() => {
          setShowBulkImport(false);
          loadData();
          showAlert("Bulk import completed successfully!");
        }}
      />

      {/* Preview Modal */}
      <PreviewModal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        countries={countries}
      />
    </div>
  );
}

// Content Create/Edit Modal
function ContentModal({ show, onHide, editingItem, mode, countries, onSuccess }) {
  const [formData, setFormData] = useState({
    countryid: "",
    context: "sandbox",
    foldername: "",
    title: "",
    contenttype: "video",
    iframe: "",
    documenturl: "",
    displayorder: 0,
    isactive: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingItem) {
      setFormData(editingItem);
    } else {
      setFormData({
        countryid: "",
        context: "sandbox",
        foldername: "",
        title: "",
        contenttype: "video",
        iframe: "",
        documenturl: "",
        displayorder: 0,
        isactive: true
      });
    }
  }, [editingItem, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (mode === "edit") {
        await updateSandboxContent(editingItem.id, formData);
      } else {
        await createSandboxContent(formData);
      }
      onSuccess();
    } catch (error) {
      alert("Error saving content: " + error.message);
    }
    setSaving(false);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{mode === "edit" ? "Edit" : "Create"} Content</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Country *</Form.Label>
                <Form.Select
                  value={formData.countryid}
                  onChange={(e) => setFormData({...formData, countryid: e.target.value})}
                  required
                >
                  <option value="">Select country...</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>{country.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Context *</Form.Label>
                <Form.Select
                  value={formData.context}
                  onChange={(e) => setFormData({...formData, context: e.target.value})}
                  required
                >
                  <option value="sandbox">Sandbox (Main Area)</option>
                  <option value="modal">Modal (Additional Resources)</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Folder Name *</Form.Label>
            <Form.Control
              type="text"
              value={formData.foldername}
              onChange={(e) => setFormData({...formData, foldername: e.target.value})}
              placeholder="e.g., In Class Videos"
              required
            />
            <Form.Text className="text-muted">
              Group related content together by using the same folder name
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Title *</Form.Label>
            <Form.Control
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="e.g., Reception Baseline Screener Walk Through"
              required
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Content Type *</Form.Label>
                <Form.Select
                  value={formData.contenttype}
                  onChange={(e) => setFormData({...formData, contenttype: e.target.value})}
                  required
                >
                  <option value="video">Video</option>
                  <option value="document">Document</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Display Order</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.displayorder}
                  onChange={(e) => setFormData({...formData, displayorder: parseInt(e.target.value)})}
                />
                <Form.Text className="text-muted">
                  Lower numbers appear first
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {formData.contenttype === "video" && (
            <Form.Group className="mb-3">
              <Form.Label>iFrame Embed Code *</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={formData.iframe || ""}
                onChange={(e) => setFormData({...formData, iframe: e.target.value})}
                placeholder="<iframe width='672' height='378' src='https://www.youtube.com/embed/...' ...></iframe>"
                required={formData.contenttype === "video"}
              />
              <Form.Text className="text-muted">
                Paste the full iframe embed code from YouTube or other video platform
              </Form.Text>
            </Form.Group>
          )}

          {formData.contenttype === "document" && (
            <Form.Group className="mb-3">
              <Form.Label>Document URL *</Form.Label>
              <Form.Control
                type="url"
                value={formData.documenturl || ""}
                onChange={(e) => setFormData({...formData, documenturl: e.target.value})}
                placeholder="https://example.com/document.pdf"
                required={formData.contenttype === "document"}
              />
              <Form.Text className="text-muted">
                Full URL to the PDF or document file
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Active (visible to users)"
              checked={formData.isactive}
              onChange={(e) => setFormData({...formData, isactive: e.target.checked})}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? "Saving..." : mode === "edit" ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// Bulk Import Modal
function BulkImportModal({ show, onHide, countries, onSuccess }) {
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, step: "" });
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const downloadTemplate = () => {
    const countryMap = {};
    countries.forEach(c => {
      countryMap[c.name.toLowerCase().replace(/\s+/g, '')] = c.id;
    });

    const template = {
      info: "Import multiple content items at once",
      countries: countryMap,
      content: [
        {
          countryid: "use-id-from-countries-above",
          context: "sandbox",
          foldername: "In Class Videos",
          title: "Reception Baseline Screener Walk Through",
          contenttype: "video",
          iframe: "<iframe width='672' height='378' src='...'></iframe>",
          displayorder: 1,
          isactive: true
        },
        {
          countryid: "use-id-from-countries-above",
          context: "sandbox",
          foldername: "Word & Image Lists",
          title: "Reception Word List",
          contenttype: "document",
          documenturl: "https://example.com/document.pdf",
          displayorder: 1,
          isactive: true
        }
      ]
    };

    const blob = new Blob([JSON.stringify(template, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sandbox-content-import-template.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    setError(null);
    setResults(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (!data.content || !Array.isArray(data.content)) {
        throw new Error("Invalid JSON format. Must have 'content' array.");
      }

      await processImport(data.content);
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const processImport = async (items) => {
    const total = items.length;
    let current = 0;
    let created = 0;
    let failed = 0;

    for (const item of items) {
      try {
        setProgress({ current, total, step: `Creating: ${item.title}` });
        await createSandboxContent(item);
        created++;
      } catch (error) {
        console.error(`Failed to create ${item.title}:`, error);
        failed++;
      }
      current++;
      setProgress({ current, total, step: `Created: ${item.title}` });
    }

    setProgress({ current: total, total, step: "Import completed!" });
    setResults({ created, failed, total });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Bulk Import Content</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <h6>Instructions:</h6>
          <ol>
            <li>Download the template JSON file</li>
            <li>Fill in your content items using the template format</li>
            <li>Make sure country IDs match your database</li>
            <li>Upload the completed JSON file</li>
          </ol>
        </div>

        <div className="mb-4">
          <Button variant="secondary" onClick={downloadTemplate}>
            <FaDownload className="me-2" />
            Download Template JSON
          </Button>
        </div>

        <Form.Group className="mb-4">
          <Form.Label>Upload JSON File</Form.Label>
          <Form.Control
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            disabled={importing}
          />
        </Form.Group>

        {importing && (
          <div className="mb-4">
            <div className="mb-2">
              <strong>Progress:</strong> {progress.current} / {progress.total}
            </div>
            <div className="progress mb-2">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
              />
            </div>
            <div className="text-muted">{progress.step}</div>
          </div>
        )}

        {error && (
          <Alert variant="danger">
            <strong>Error:</strong> {error}
          </Alert>
        )}

        {results && (
          <Alert variant={results.failed > 0 ? "warning" : "success"}>
            <h6>Import Complete!</h6>
            <ul className="mb-0">
              <li>Total items: {results.total}</li>
              <li>Successfully created: {results.created}</li>
              <li>Failed: {results.failed}</li>
            </ul>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

// Preview Modal
function PreviewModal({ show, onHide, countries }) {
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedContext, setSelectedContext] = useState("sandbox");
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadPreview = async () => {
    if (!selectedCountry) return;
    
    setLoading(true);
    try {
      const data = selectedContext === "sandbox" 
        ? await getPublicSandboxContent(selectedCountry)
        : await getPublicModalContent(selectedCountry);
      setPreviewData(data);
    } catch (error) {
      alert("Error loading preview: " + error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (show && selectedCountry) {
      loadPreview();
    }
  }, [selectedCountry, selectedContext, show]);

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Preview Content</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Select Country</Form.Label>
              <Form.Select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
              >
                <option value="">Choose a country...</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>{country.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Context</Form.Label>
              <Form.Select
                value={selectedContext}
                onChange={(e) => setSelectedContext(e.target.value)}
              >
                <option value="sandbox">Sandbox</option>
                <option value="modal">Modal</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {loading && <div className="text-center">Loading preview...</div>}

        {!loading && previewData.length === 0 && selectedCountry && (
          <Alert variant="info">No content found for this country and context.</Alert>
        )}

        {!loading && previewData.length > 0 && (
          <div>
            <h6>Preview: This is how users will see the content</h6>
            {previewData.map((folder, idx) => (
              <Card key={idx} className="mb-3">
                <Card.Header>
                  <strong>{folder.folderName}</strong>
                </Card.Header>
                <Card.Body>
                  <Table size="sm">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {folder.cards.map((card, cardIdx) => (
                        <tr key={cardIdx}>
                          <td>{card.title}</td>
                          <td>
                            {card.iframe ? <Badge bg="primary">Video</Badge> : <Badge bg="secondary">Document</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default SandboxContentManagement;